import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, Observable } from 'rxjs';

import { NotificationsHubClient } from '@tfs/notifications';

import { ToolImportStatus } from './models/data-import.model';
import {
  ToolImportJobStatusChangeEvent,
  ToolImportJobStatusChangePayload,
  isToolImportJobStatusChangeEvent,
} from './models/tool-import-events.model';

const TOOL_IMPORT_NOTIFICATIONS_STORAGE_KEY = 'tfs.tool-import.notifications';
const MAX_TOOL_IMPORTS = 100;

@Injectable({ providedIn: 'root' })
export class ToolImportNotifications {
  private readonly notificationsHub = inject(NotificationsHubClient);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _toolImports = signal<readonly ToolImportJobStatusChangePayload[]>(
    loadToolImportsFromStorage()
  );

  readonly toolImports = this._toolImports.asReadonly();

  constructor() {
    this.notificationsHub.notifications$
      .pipe(filter(isToolImportJobStatusChangeEvent), takeUntilDestroyed(this.destroyRef))
      .subscribe(toolImportEvent => {
        const incomingImport = toolImportEvent.data;
        const otherImports = this._toolImports().filter(
          existingImport => existingImport.toolImportId !== incomingImport.toolImportId
        );

        if (incomingImport.status === ToolImportStatus.Removed) {
          this.update(otherImports);
          return;
        }

        this.update([...otherImports, incomingImport].slice(-MAX_TOOL_IMPORTS));
      });
  }

  dismissImport(toolImportId: string): void {
    this.update(
      this._toolImports().filter(existingImport => existingImport.toolImportId !== toolImportId)
    );
  }

  /**
   * Filtered stream for tool import job completion (Completed or Failed status)
   * for a specific import ID.
   *
   * @param toolImportId - The tool import ID to filter for
   * @returns Observable of matching ToolImportJobStatusChangeEvent when job reaches terminal state
   */
  watchJobCompletion(toolImportId: string): Observable<ToolImportJobStatusChangeEvent> {
    return this.notificationsHub.notifications$.pipe(
      filter(isToolImportJobStatusChangeEvent),
      filter(event => event.data.toolImportJobId === toolImportId),
      filter(
        event =>
          event.data.status === ToolImportStatus.Completed ||
          event.data.status === ToolImportStatus.Failed
      )
    );
  }

  private update(nextToolImports: readonly ToolImportJobStatusChangePayload[]): void {
    this._toolImports.set(nextToolImports);
    persistToolImportsToStorage(nextToolImports);
  }
}

function loadToolImportsFromStorage(): readonly ToolImportJobStatusChangePayload[] {
  try {
    const raw = sessionStorage.getItem(TOOL_IMPORT_NOTIFICATIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isPersistedToolImport)
      .filter(entry => entry.status !== ToolImportStatus.Removed)
      .slice(-MAX_TOOL_IMPORTS);
  } catch {
    return [];
  }
}

function isPersistedToolImport(entry: unknown): entry is ToolImportJobStatusChangePayload {
  if (typeof entry !== 'object' || entry === null) return false;
  const candidate = entry as { toolImportId?: unknown; status?: unknown };
  return typeof candidate.toolImportId === 'string' && typeof candidate.status === 'string';
}

function persistToolImportsToStorage(
  toolImports: readonly ToolImportJobStatusChangePayload[]
): void {
  try {
    sessionStorage.setItem(TOOL_IMPORT_NOTIFICATIONS_STORAGE_KEY, JSON.stringify(toolImports));
  } catch {
    // Best-effort: quota exceeded or storage policy denial — in-memory state still works.
  }
}
